import { z } from "zod";
import { RequestConfig, rest } from "src/common/rest";
import { restWithObjectVersion } from "src/common/restWithObjectVersion";
import { apiAdminUrl, apiAdminUrlV2 } from "src/common/apiUrl";
import { ZObjState, zObjState } from "src/types/ZObjState";
import { ZUid } from "src/types/zUid";
import {
  ZRolesGroup,
  ZPermissions,
  ZTypeActions,
  zRolesGroup,
  zTypeActions,
  zPermissionGroupItem,
} from "./roleTypes";
import { EdObjStates } from "../EdObject";

export const loadRoleGroups = async (
  config?: RequestConfig,
): Promise<ZRolesGroup[]> => {
  const resp = await restWithObjectVersion.get(
    apiAdminUrlV2("/groups"),
    config,
  );
  return zRolesGroup.array().parse(resp.data);
};

export const loadTypeActions = async (): Promise<ZTypeActions[]> => {
  const resp = await rest.get(
    apiAdminUrl("/actions?types=OBJECT&types=ENTITY&types=ATTRIBUTE"),
  );
  return zTypeActions.array().parse(resp.data);
};

export const loadObjectPermissions = async (
  objectId: ZUid,
  stateId: ZUid,
): Promise<ZPermissions> => {
  const resp = await rest.get(apiAdminUrl("/permissions"), {
    params: { objectId, stateId },
  });
  return zPermissionGroupItem.array().parse(resp.data);
};

export const saveObjectPermissions = async (
  stateId: ZUid,
  objectId: ZUid,
  roles: ZPermissions,
) => {
  await rest.put(apiAdminUrl("/permissions"), roles, {
    params: { stateId, objectId },
  });
};

export const savePermissionOnMnemonicGroup = async (
  attributeId: ZUid,
  stateId: ZUid,
) => {
  await rest.put(
    apiAdminUrl("/permissions/copy-for-mnemonic-group"),
    undefined,
    { params: { attributeId, stateId } },
  );
};

export const saveObjectRolesMap = async (
  objectId: ZUid,
  rolesMap: EdObjStates,
) =>
  Promise.all(
    Object.entries(rolesMap).map(([stateId, permissions]) =>
      saveObjectPermissions(stateId, objectId, permissions),
    ),
  );

export const loadAttributePermissions = async (
  attributeId: ZUid,
  stateId: ZUid,
): Promise<ZPermissions> => {
  const resp = await rest.get(apiAdminUrl("/permissions"), {
    params: {
      attributeId,
      stateId,
    },
  });
  return zPermissionGroupItem.array().parse(resp.data);
};

export const saveAttributePermissions = async (
  attributeId: ZUid,
  stateId: ZUid,
  roles: ZPermissions,
) => {
  await rest.put(apiAdminUrl("/permissions"), roles, {
    params: {
      attributeId,
      stateId,
    },
  });
};

export const loadObjectStates = async (
  objectId: ZUid,
  options: {
    translate?: boolean;
    isRefLikeAttr?: boolean;
  } = {},
): Promise<ZObjState[]> => {
  const { isRefLikeAttr } = options;
  const resp = await restWithObjectVersion.get(apiAdminUrl("/states"), {
    params: { objectId, isRefLikeAttr },
    translate: options.translate,
  });
  return zObjState.array().min(1).parse(resp.data);
};

/**
 * Для загрузки статусов из компонентов редактирования,
 * которые обрабатывают атрибуты с типами :
 * -AttrTypeName.object
 * -AttrTypeName.childEntities
 * согласно задаче http://jira.d4r.int/browse/LPLM-1851
 */
export const loadObjectStatesInsideRefLikeAtts = async (
  objectId: ZUid,
): Promise<ZObjState[]> =>
  loadObjectStates(objectId, { translate: true, isRefLikeAttr: true });

export const makeObjectStatesMap = (list: ZObjState[]) =>
  list.reduce(
    (acc, curr) => {
      acc[curr.id] = curr.name;
      return acc;
    },
    {} as Record<ZUid, string>,
  );

export const createObjectState = async (
  objectId: ZUid,
  name: string,
): Promise<ZObjState> => {
  const resp = await rest.post(apiAdminUrl("/states"), [name], {
    params: { objectId },
  });
  const result = z.tuple([zObjState]).parse(resp.data);
  return result[0];
};

export const deleteObjectState = async (stateId: ZUid): Promise<void> => {
  await rest.delete(apiAdminUrl(`/states/${stateId}`));
};

/**
 * Эта функция вызывается после сабмита формы объекта.
 * Там пользователь мог удалить какие-то существующие состояния.
 * Но они удалились только из списка states.
 * А после сабмита это нужно выполнить и на серверной стороне.
 * Для этого получаем полный список состояний, сравниваем с тем который пришел из формы и удаляем разницу
 * @param objectId
 * @param states Это список состояний, которые остались после редактирования (они не удаляются)
 */
export const smartDeleteObjectStates = async (
  objectId: ZUid,
  states: ZObjState[],
): Promise<void> => {
  const oldStates: ZObjState[] = await loadObjectStates(objectId);
  // Здесь собираем id тех состояний, которые нужно удалять
  const oldStateIds = new Set(oldStates.map(({ id }) => id));
  states.forEach(({ id }) => oldStateIds.delete(id));
  if (oldStateIds.size > 0) {
    await Promise.all(
      Array.from(oldStateIds).map((stateId) => deleteObjectState(stateId)),
    );
  }
};
